// RELEASE-TENIS10 § 11 P77 (Tudor 2026-10-26, canvas A2) — the tenis10.ro families a Users search names too, in a section of
// their own under the accounts: a parent with no Sportya account yet, children not claimed yet (or hidden by the parent), the
// families already claimed muted, with the link to their account. Each family is an account's card (P50) with the tenis10.ro
// edge — its parent, contact, account and invitation, then every child with its number, year, level and state, the family's
// counts beside — „Open in Claims” opens its page, „View all N in Claims” the register with the same search. The search runs
// from 3 characters on the list's own pause (no keystroke reaches the API), and an older answer never replaces a newer one.
// P103 (Tudor 2026-10-27, canvas B): the family's tenis10.ro id reads „T10#33245”; under a plain number (the Sportya numbers
// only) a line offers the same search written T10#… — the Users search runs it in place.
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Spinner } from "reactstrap"
import CopyContact from "components/Common/CopyContact"
import { searchFamilies } from "pages/Tenis10Claims/api"
import { childNumber, invitationBadges, legacyIdSearched, legacyIdText, levelText } from "pages/Tenis10Claims/claimsLabels"
import LegacyId, { LegacySearchTip } from "pages/Tenis10Claims/LegacyId"

const REGISTER_PATH = "/accounts/tenis10-claims"
export const TENIS10_SEARCH_MIN = 3
export const TENIS10_FAMILIES_SHOWN = 5

const fold = text => String(text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
// P103: a tenis10.ro id searched (`T10#33245`) marks the „T10#33245” it found, never a number that only starts like it
const wordsOf = search => (legacyIdSearched(search) !== null ? [`t10${legacyIdSearched(search)}`] : fold(search).split(/[^a-z0-9]+/).filter(Boolean))

/** `text` with each word that starts like a word of the search marked (a name, a #number). */
export const Highlight = ({ text, search }) => {
  const words = wordsOf(search)
  return String(text || "").split(/(\s+|-)/).map((part, index) => (part.trim() && words.some(word => fold(part).replace(/[^a-z0-9]/g, "").startsWith(word))
    ? <mark key={index} className="player-t10-mark">{part}</mark>
    : <React.Fragment key={index}>{part}</React.Fragment>))
}

/** The families `search` names (3 characters at least): `{ status: idle | loading | ready | error, data, retry }`. */
export function useTenis10Families(search) {
  const q = String(search || "").trim()
  const [state, setState] = useState({ status: "idle" })
  const request = useRef(0)
  const load = useCallback(() => {
    const id = ++request.current
    if (q.length < TENIS10_SEARCH_MIN) return setState({ status: "idle" })
    setState({ status: "loading" })
    return searchFamilies({ q, limit: TENIS10_FAMILIES_SHOWN })
      .then(data => request.current === id && setState({ status: "ready", data }))
      .catch(() => request.current === id && setState({ status: "error" }))
  }, [q])
  useEffect(() => {
    load()
    return () => { request.current += 1 }
  }, [load])
  return { ...state, retry: load }
}

const parentName = family => [family.parent?.firstName, family.parent?.lastName].filter(Boolean).join(" ") || "—"
const initials = family => `${(family.parent?.firstName || "?").charAt(0)}${(family.parent?.lastName || "").charAt(0)}`.toUpperCase()
const childrenText = count => `${count} ${count === 1 ? "child" : "children"}`
const totalText = data => (data.totalExact === false ? `${data.total}+` : Number(data.total || 0).toLocaleString("ro-RO"))
const claimsLink = search => `${REGISTER_PATH}?q=${encodeURIComponent(search)}`

// each child's state in its colour and icon; the family's counts beside its children's heading, in the same words
const STATES = {
  unclaimed: ["warning", "bx-time-five", "Unclaimed", n => `${n} unclaimed`],
  hiddenByParent: ["danger", "bx-hide", "Hidden by parent", n => `${n} hidden by parent`],
  hiddenBySupport: ["secondary", "bx-hide", "Hidden by support", n => `${n} hidden by support`],
  hiddenByAccount: ["secondary", "bx-hide", "Hidden · account deleted", n => `${n} hidden · account deleted`],
  claimed: ["success", "bx-check", "Claimed", n => `${n} claimed`],
  forwarded: ["info", "bx-send", "Forwarded 16+", n => `${n} forwarded 16+`],
}
const HIDDEN_BY = { parent: "hiddenByParent", support: "hiddenBySupport", account: "hiddenByAccount" }
const stateOf = child => (child.state === "hidden" ? HIDDEN_BY[child.hiddenBy] || "hiddenBySupport" : STATES[child.state] ? child.state : "unclaimed")
// counted child by child (the register's own counts take a forwarded 16+ as unclaimed too)
const countsOf = family => (family.profiles || []).reduce((counts, child) => ({ ...counts, [stateOf(child)]: (counts[stateOf(child)] || 0) + 1 }), {})
// a claimed family: bound to an account that holds every child — muted, the link to the account kept
const claimedFamily = family => Boolean(family.boundTo) && family.profiles?.length > 0 && family.profiles.every(child => child.state === "claimed")
const StateBadge = ({ state, count }) => {
  const [tone, icon, label, counted] = STATES[state]
  return <span className={`badge badge-soft-${tone} player-t10-state`}><i className={`bx ${icon}`} aria-hidden="true" /> {count ? counted(count) : label}</span>
}

const FamilyIdentity = ({ family, search }) => (
  <div className="player-identity-cell">
    <span className="player-t10-avatar" aria-hidden="true">{initials(family)}</span>
    <div className="min-w-0">
      <span className="player-list-name text-dark"><Highlight text={parentName(family)} search={search} /></span>
      <div className="player-list-meta"><span><LegacyId id={family.legacyUserId}><Highlight text={legacyIdText(family.legacyUserId)} search={search} /></LegacyId></span>
        <span>{family.city || "City not set"}</span></div>
      <div className="player-list-personas mt-1">
        <span className="badge badge-soft-warning mr-1">tenis10.ro parent</span>
        <span className="text-muted font-size-12">{childrenText(family.children?.total || 0)}</span>
      </div>
    </div>
  </div>
)

const FamilyContact = ({ family }) => (
  <div className="player-contact-cell">
    {family.parent?.email ? <CopyContact value={family.parent.email} label="email" icon="bx bx-envelope" /> : <span className="text-muted">Email not set</span>}
    {family.parent?.phone ? <CopyContact value={family.parent.phone} label="phone" icon="bx bx-phone" /> : <span className="text-muted">Phone not set</span>}
  </div>
)

const FamilyAccount = ({ family, listState }) => (family.boundTo
  ? <div className="player-account-cell"><span className="text-muted font-size-12">Sportya account</span>
    <Link to={{ pathname: `/accounts/players/${family.boundTo.accountId}`, state: listState }} className="font-size-13">
      #{family.boundTo.userId}{family.boundTo.name ? ` · ${family.boundTo.name}` : ""}</Link></div>
  : <div className="player-account-cell"><span className="badge badge-soft-secondary align-self-start">No Sportya account</span>
    <span className="text-muted font-size-12">The parent claims with the family link</span></div>)

const FamilyInvitation = ({ family }) => <div className="player-account-cell">
  <div>{invitationBadges(family).map(([tone, text]) => <span key={text} className={`badge badge-soft-${tone} mr-1 mb-1`}>{text}</span>)}</div>
  <span className="text-muted font-size-12">Migration invitation</span>
</div>

const FamilyChildren = ({ family, search }) => {
  const counts = countsOf(family)
  return <section className="player-children-strip" aria-label={`Children of family ${legacyIdText(family.legacyUserId)}`}>
    <div className="player-children-heading"><strong>{childrenText((family.profiles || []).length)} on tenis10.ro</strong>
      {Object.keys(STATES).filter(state => counts[state] > 0).map(state => <StateBadge key={state} state={state} count={counts[state]} />)}
    </div>
    <div className="player-children-grid">
      {(family.profiles || []).map(child => <div key={child.profileId} className={`player-child-card player-t10-child-${stateOf(child)}`}>
        {/* P78: the child's own number after its name, as on the cards under a Sportya parent */}
        <strong className="player-child-name"><Highlight text={`${child.firstName} ${child.lastName}`} search={search} />
          {childNumber(child) != null && <> <small className="text-muted font-weight-normal"><Highlight text={`#${childNumber(child)}`} search={search} /></small></>}</strong>
        <span className="player-child-secondary">
          <small className="text-muted">{[child.birthYear && `born ${child.birthYear}`, levelText(child)].filter(Boolean).join(" · ")}</small>
          <StateBadge state={stateOf(child)} />
        </span>
      </div>)}
    </div>
  </section>
}

/** One family: its head row and its children's row under it, one card (P50) with the tenis10.ro edge. */
const FamilyRows = ({ family, search, listState }) => {
  const muted = claimedFamily(family) ? " player-t10-claimed" : ""
  return <>
    <tr className={`player-t10-family has-children-found${muted}`}>
      <td><FamilyIdentity family={family} search={search} /></td>
      <td><FamilyContact family={family} /></td>
      <td><FamilyAccount family={family} listState={listState} /></td>
      <td><FamilyInvitation family={family} /></td>
      <td className="text-right"><div className="player-actions-cell directory-row-actions">
        <Link to={{ pathname: `${REGISTER_PATH}/families/${family.invitationId}`, state: { registerSearch: `?q=${encodeURIComponent(search)}` } }}
          className="btn btn-outline-primary btn-sm player-t10-open" aria-label={`Open family ${legacyIdText(family.legacyUserId)} in Tenis10 Claims`}>Open in Claims</Link>
      </div></td>
    </tr>
    <tr className={`player-t10-family-children${muted}`}><td colSpan={5} className="player-children-row"><FamilyChildren family={family} search={search} /></td></tr>
  </>
}

const COLUMNS = [["tenis10.ro parent", "27%"], ["Contact", "23%"], ["Sportya account", "20%"], ["Migration", "18%"], ["Actions", "12%"]]

/**
 * The section under the Users list: nothing below 3 characters; else the families the search names, the first 5, with their
 * count and the way to the rest in Tenis10 Claims. `accountsFound` — the Sportya accounts found (the empty line's words).
 */
export default function Tenis10Families({ search, accountsFound = 0, listState, onSearch }) {
  const q = String(search || "").trim()
  const { status, data, retry } = useTenis10Families(q)
  if (status === "idle") return null
  const families = status === "ready" ? data.families || [] : []
  return <section className="player-t10-section" aria-label="tenis10.ro families">
    <div className="player-t10-head">
      <div>
        <h5 className="mb-1"><i className="bx bx-history mr-1" aria-hidden="true" />tenis10.ro families
          {status === "ready" && <span className="badge badge-soft-warning ml-2">{totalText(data)}</span>}</h5>
        <p className="text-muted mb-0 font-size-13">Migrated from tenis10.ro, not on Sportya yet or with children not claimed: support works them in Tenis10 Claims.
          {" "}A tenis10.ro ID is searched as T10#33245.</p>
      </div>
      {status === "ready" && data.total > 0 && <Link to={claimsLink(q)} className="btn btn-light btn-sm text-nowrap">
        View all {totalText(data)} in Claims <i className="bx bx-right-arrow-alt" aria-hidden="true" /></Link>}
    </div>
    {status === "loading" && <p className="player-t10-line" role="status"><Spinner size="sm" className="mr-2" />Searching the tenis10.ro families…</p>}
    {status === "error" && <p className="player-t10-line text-danger" role="alert">The tenis10.ro families didn't load.{" "}
      <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={retry}>Retry</button></p>}
    {status === "ready" && !families.length && <p className="player-t10-line" role="status">
      No tenis10.ro family matches „{q}”{accountsFound ? "" : " either"}.</p>}
    {families.length > 0 && <>
      <div className="table-responsive player-directory-table">
        <table className="table align-middle player-directory-records player-t10-records" aria-label="tenis10.ro families found">
          <thead className="thead-light"><tr>{COLUMNS.map(([text, width], index) => <th key={text} scope="col" style={{ width }}
            className={index === COLUMNS.length - 1 ? "text-right" : undefined}>{text}</th>)}</tr></thead>
          <tbody>{families.map(family => <FamilyRows key={family.invitationId} family={family} search={q} listState={listState} />)}</tbody>
        </table>
      </div>
      <p className="player-t10-line">Showing <strong>{families.length}</strong> of <strong>{totalText(data)}</strong> {data.total === 1 ? "family" : "families"}
        {data.total > families.length && <> · <Link to={claimsLink(q)}>view the rest in Tenis10 Claims</Link></>}</p>
    </>}
    {status === "ready" && onSearch && <LegacySearchTip search={q} onSearch={onSearch} className="player-t10-line" />}
  </section>
}
